import React, { Fragment } from 'react';
import { withCookies } from 'react-cookie';
import { withRouter } from 'react-router-dom';

import { Tooltip, Typography, Paper, Button, IconButton } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import { Constants } from 'form-builder';

import { ThemeColors } from '../Theme';
import { ConstantPopover } from '../components/ConstantPopover';
import { RestComponent } from 'react-frontend-utils';

export class ConstantsPage extends RestComponent {

    styles = {
        paperLabel: {
            marginLeft: 5,
            color: 'gray',
            fontSize: '14pt',
            flexGrow: 1
        },
        paper: {
            padding: 10,
            minHeight: 500,
            backgroundColor: ThemeColors.templateGray
        },
        table: {
            borderCollapse: 'collapse',
            width: '100%',
            marginBottom: 10,
            marginTop: 10
        },
        tableHeader: {
            borderBottom: '2px solid ' + ThemeColors.appBarBackground,
            textAlign: 'left',
            paddingBottom: 5,
            paddingRight: 10,
            marginBottom: 8,
            fontSize: 13,
            color: ThemeColors.darkGray,
            fontWeight: 'normal',
            textTransform: 'uppercase'
        },
        tableData: {
            textAlign: 'left',
            fontSize: 14,
            paddingRight: 10,
            verticalAlign: 'center'
        },
        constantsEditButton: {
            padding: 4,
            marginRight: 5
        },
        constantsDeleteButton: {
            padding: 4
        }
    };

    constructor(props) {
        super(props);

        this.state.constants = new Constants();
        this.state.addConstantPopupOpen = false;
        this.state.editConstantName = null;
    }

    componentDidMount() {
        super.componentDidMount();
        this._fetchConstants();
    }

    _fetchConstants = () => {
        this.incrementBusy();
        this.secureJSONFetch('/ap/constants', {}, this._fetchConstantsCallback, this._fetchErrorCallback);
    }

    _fetchConstantsCallback = (response) => {
        const constants = new Constants();

        if (response) {
            response.forEach((entry) => {
                if (entry && entry.name)
                    constants.addConstant(entry.name, entry.value, entry.description);
            });
        }

        this.setState({constants: constants});
        this.decrementBusy();
    }

    _fetchErrorCallback = (error) => {
        this.showConfirmAlert('Error', error, 'red');
        this.decrementBusy();
    }

    _addConstant = () => {
        this.setState({addConstantPopupOpen: true, editConstantName: null});
    }

    _editConstant = (name) => {
        this.setState({
            addConstantPopupOpen: true,
            editConstantName: name
        });
    }

    _addConstantOkCallback = (name, value, description) => {
        const editingConstantName = this.state.editConstantName;
        const trimmedName = name ? name.trim() : '';
        if (!trimmedName) {
            this.showConfirmAlert('Constant Name Error', 'Constant name cannot be blank', 'red');
            return;
        }

        if (!trimmedName.startsWith('AGS_')) {
            this.showConfirmAlert('Constant Name Error', 'Global constant name must start with "AGS_"', 'red');
            return;
        }

        if (/\s/.test(trimmedName)) {
            this.showConfirmAlert('Constant Name Error', 'Global constant name cannot contain whitespace', 'red');
            return;
        }

        if (trimmedName.length > 40) {
            this.showConfirmAlert('Constant Name Error', 'Constant name cannot exceed 40 characters', 'red');
            return;
        }

        const trimmedValue = value ? value.trim() : '';

        let valueToStore;
        if (trimmedValue.startsWith('"') && trimmedValue.endsWith('"')) {
            valueToStore = trimmedValue.substring(1, trimmedValue.length - 1);
        }
        else {
            const numericValue = Number(trimmedValue);
            if (trimmedValue === '' || Number.isNaN(numericValue)) {
                this.showConfirmAlert('Constant Value Error', 'Value must be a number or a quoted string ("MyConstant")', 'red');
                return;
            }
            valueToStore = numericValue;
        }

        if (editingConstantName) {
            if (editingConstantName !== trimmedName && this.state.constants.hasConstant(trimmedName)) {
                this.showConfirmAlert('Constant Name Error', 'A constant with this name already exists', 'red');
                return;
            }
        }
        else {
            if (this.state.constants.hasConstant(trimmedName)) {
                this.showConfirmAlert('Constant Name Error', 'A constant with this name already exists', 'red');
                return;
            }
        }

        this._postConstant(trimmedName, valueToStore, description, editingConstantName);
    }

    _deleteConstant = (name) => {
        this._deleteConstantRequest(name);
    }

    _postConstant = (name, value, description, editingConstantName = null) => {
        this.incrementBusy();
        const constant = {name: name, value: value, description: description};
        this.secureJSONFetch('/ap/constants', {method: 'POST', body: JSON.stringify(constant)},
                             () => this._postConstantCallback(name, editingConstantName), this._postOrDeleteErrorCallback);
    }

    _postConstantCallback = (name, editingConstantName) => {
        if (editingConstantName && editingConstantName !== name) {
            this.secureJSONFetch('/ap/constants/' + encodeURIComponent(editingConstantName), {method: 'DELETE'},
                                 this._postOrDeleteSuccessCallback, this._postOrDeleteErrorCallback);
            return;
        }

        this._postOrDeleteSuccessCallback();
    }

    _deleteConstantRequest = (name) => {
        this.incrementBusy();
        this.secureJSONFetch('/ap/constants/' + encodeURIComponent(name), {method: 'DELETE'},
                             () => this._deleteConstantCallback(name), this._postOrDeleteErrorCallback);
    }

    _postOrDeleteSuccessCallback = () => {
        this.setState({addConstantPopupOpen: false, editConstantName: null});
        this.decrementBusy();
        this._fetchConstants();
    }

    _postOrDeleteErrorCallback = (error) => {
        this.showConfirmAlert('Error', error, 'red');
        this.decrementBusy();
        this._fetchConstants();   // refetch constants to ensure the UI is in sync with the server after a failed delete/post
    }

    _deleteConstantCallback = (name) => {
        const updatedConstants = this.state.constants;
        updatedConstants.removeConstant(name);
        this.setState({constants: updatedConstants});
        this.decrementBusy();
    }

    _renderConstantRow = (name, constant, key) => {
        const value = constant?.value;
        const description = constant?.description ?? '';

        if (!name) {
            return (
                <tr key={key}>
                    <th style={this.styles.tableHeader}>Name</th>
                    <th style={this.styles.tableHeader}>Value</th>
                    <th style={this.styles.tableHeader}>Description</th>
                    <th style={{...this.styles.tableHeader, paddingRight: 0, width: '30px'}}/>
                </tr>
            );
        }

        return (
            <Fragment key={key}>
                <tr>
                    <td style={this.styles.tableData}>{name}</td>
                    <td style={this.styles.tableData}>{typeof value === 'string' ? '"' + value + '"' : value}</td>
                    <td style={this.styles.tableData}>{description}</td>

                    <td style={{...this.styles.tableData, paddingRight: 0}}>
                        <div style={{display: 'flex', justifyContent: 'right'}}>
                            <Tooltip title="Edit this Constant">
                                <IconButton size='small' style={this.styles.constantsEditButton} onClick={() => this._editConstant(name)}>
                                    <EditIcon style={{color: 'gray'}}/>
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete this Constant">
                                <IconButton size='small' style={this.styles.constantsDeleteButton} onClick={() => this._deleteConstant(name)}>
                                    <DeleteIcon style={{color: 'red'}}/>
                                </IconButton>
                            </Tooltip>
                        </div>
                    </td>
                </tr>
            </Fragment>
        );
    }

    render() {

        const buttonStyle = {
            borderColor: ThemeColors.addColor,
            color: ThemeColors.addColor,
            maxWidth: 150,
            marginTop: 20,
            marginBottom: 10
        };

        let editConstantValue = "";
        let editConstantDescription = "";
        if (this.state.editConstantName && this.state.constants.hasConstant(this.state.editConstantName)) {
            const value = this.state.constants.getConstant(this.state.editConstantName);
            editConstantValue = typeof value === 'string' ? '"' + value + '"' : String(value);
            editConstantDescription = this.state.constants.getDescription(this.state.editConstantName) ?? '';
        }

        return (
            <Fragment>
                {this.getConfirmAlertComponent()}

                <ConstantPopover isOpen={this.state.addConstantPopupOpen}
                                 title={this.state.editConstantName ? 'Edit Constant' : 'Add Constant'}
                                 okLabel={this.state.editConstantName ? 'Save' : 'OK'}
                                 constantName={this.state.editConstantName ? this.state.editConstantName : ''}
                                 constantValue={editConstantValue}
                                 constantDescription={editConstantDescription}
                                 okCallback={this._addConstantOkCallback}
                                 cancelCallback={() => this.setState({addConstantPopupOpen: false, editConstantName: null})} />

                <Paper style={this.styles.paper}>
                    <Typography style={this.styles.paperLabel} variant="h6">Global Constants</Typography>

                    <Button fullWidth
                            size='small'
                            style={buttonStyle}
                            onClick={this._addConstant}
                            variant='outlined'
                            component='label'>
                        Add Constant
                    </Button>

                    <table style={this.styles.table}>
                        <thead>
                            {this._renderConstantRow(null, 0, 'header')}
                        </thead>
                        <tbody>
                            {Array.from(this.state.constants.entries()).map(([constantName, constantValue], index) =>
                                this._renderConstantRow(constantName, constantValue, index + 1)
                            )}
                        </tbody>
                    </table>

                    {this.state.isBusy ? this.getBusyComponent('right', {marginLeft: 20}, 30) : null}
                </Paper>
            </Fragment>
        );
    }
}

export default withCookies(withRouter(ConstantsPage));
